import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { syncTokenToNativeApp } from '../utils/nativeCallTracking';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('crm_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use refs to prevent infinite loops
  const interceptorsSetup = useRef(false);
  const fetchingUser = useRef(false);
  const initialFetchDone = useRef(false);

  // Create stable api instance
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return instance;
  }, []);

  // Set up interceptors once
  useEffect(() => {
    if (interceptorsSetup.current) return;
    interceptorsSetup.current = true;

    // Add token to requests
    api.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem('crm_token');
      if (storedToken) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
      return config;
    });

    // Handle 401 errors - just clear state, don't redirect
    api.interceptors.response.use(
      (response) => response,
      (err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem('crm_token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
  }, [api]);

  // Fetch user data - only once on mount
  useEffect(() => {
    // Prevent multiple fetches
    if (initialFetchDone.current || fetchingUser.current) {
      return;
    }

    const storedToken = localStorage.getItem('crm_token');
    
    if (!storedToken) {
      setLoading(false);
      initialFetchDone.current = true;
      return;
    }

    fetchingUser.current = true;

    api.get('/auth/me')
      .then((response) => {
        setUser(response.data);
        setError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch user:', err);
        localStorage.removeItem('crm_token');
        setToken(null);
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
        fetchingUser.current = false;
        initialFetchDone.current = true;
      });
  }, [api]);

  const getErrorMsg = (err, defaultMsg) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return defaultMsg;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) {
      const field = detail[0].loc?.slice(-1)[0];
      return field ? `${field}: ${detail[0].msg}` : detail[0].msg;
    }
    if (detail?.msg) return detail.msg;
    return defaultMsg;
  };

  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('crm_token', access_token);
      setToken(access_token);
      setUser(userData);
      syncTokenToNativeApp(access_token);
      
      return { success: true };
    } catch (err) {
      const message = getErrorMsg(err, 'Login failed');
      setError(message);
      return { success: false, error: message };
    }
  }, [api]);

  const register = useCallback(async (userData) => {
    try {
      setError(null);
      const response = await api.post('/auth/register', userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('crm_token', access_token);
      setToken(access_token);
      setUser(newUser);
      
      return { success: true };
    } catch (err) {
      const message = getErrorMsg(err, 'Registration failed');
      setError(message);
      return { success: false, error: message };
    }
  }, [api]);

  const logout = useCallback(() => {
    localStorage.removeItem('crm_token');
    setToken(null);
    setUser(null);
  }, []);

  const isManager = user?.role === 'Super Admin' || user?.role === 'CEO' || user?.role === 'Manager';
  const isSuperAdmin = user?.role === 'Super Admin';
  const isExecutive = user?.role === 'Executive';

  const value = useMemo(() => ({
    user,
    token,
    loading,
    error,
    login,
    register,
    logout,
    isManager,
    isSuperAdmin,
    isExecutive,
    api,
  }), [user, token, loading, error, login, register, logout, isManager, isSuperAdmin, isExecutive, api]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
